import React, { useState, useMemo, useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { TabProvider } from '@tecuity/granite-ui/TabProvider'
import { TabPanel } from '@tecuity/granite-ui/TabPanel'
import { TabButton } from '@tecuity/granite-ui/TabButton'
import { useRequestData } from '../../../Utilities/hooks'
import { SET_TRADEMARK_HISTORY, SET_RECORD_DETAILS } from '../RecordDetailsReducer'
import { editNote, deleteNote, saveNewNote, getEntityNotes } from '../RecordDetailsActions'
import NotesPanel from '../NotesPanel'
import RecordHistory from 'components/RecordHistory/RecordHistory'
import { Body } from 'components/Shell/Shell'
import { Spinner } from 'components/Loaders/Loaders'
import GenericDetails from '../GenericDetails'
import * as api from '../../../requests'
import RecordDetailPageBackButton from '../RecordDetailPageBackButton'
import CharityBusinessPanel from './CharityBusinessPanel'

const CharityRecordDetails = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const route = 'CharityRecordDetails'
  const { recordDetails, recordAmendmentHistory, recordHistoryList } = useSelector(
    (state) => state.record,
    shallowEqual,
  )
  const RecordDetailsSettings = useRequestData(
    (cb, ecb) => api.GET_RECORD_DETAIL_SETTING_BY_TYPE(route, cb, ecb),
    [],
    {},
  )
  const [activeTabId, setActiveTabId] = useState('tab-1')
  const [loading, setLoading] = useState(false)

  const charityNum = useMemo(() => {
    const parts = location.pathname.split('/')
    return parts[parts.length - 1]
  }, [location.pathname])

  useEffect(() => {
    setLoading(true)
    api.FETCH_RECORD_DETAILS_BY_ROUTE(
      route,
      charityNum,
      (record) => {
        dispatch({ type: SET_RECORD_DETAILS, record: record })
        dispatch({ type: SET_TRADEMARK_HISTORY, history: record.history })
        setLoading(false)
      },
      (err) => {
        console.error(err)
        setLoading(false)
      },
    )
  }, [charityNum])

  useEffect(() => {
    if (recordDetails.CharityRecordId) dispatch(getEntityNotes(1300, recordDetails.CharityRecordId))
  }, [recordDetails.CharityRecordId])

  const isUsingBusinessExtension = useMemo(() => {
    const { Tabs } = RecordDetailsSettings
    if (!Tabs) return false
    return !!(Tabs || []).find((tab) => tab.Title === 'Business' && tab.IsEnabled)
  }, [RecordDetailsSettings])

  return (
    <TabProvider activeTabId={activeTabId} onChange={(id) => setActiveTabId(id)}>
      <Body className="trademark-record-details">
        <div className="row header-row">
          <h1>Charitable Organization Details</h1>
          <div className="tablist" role="tablist">
            <TabButton id="tab-1" forPanelId="panel-1" label="History" />
            <TabButton
              id="tab-2"
              forPanelId="panel-2"
              label="Notes"
              // customCss={
              //   recordDetails['SeeNotesFlag'] === true
              //     ? { backgroundColor: 'red !important', color: '#fff !important' }
              //     : {}
              // }
            />
            {!loading && isUsingBusinessExtension ? (
              <TabButton id="tab-3" forPanelId="panel-3" label="Business" />
            ) : null}
          </div>
        </div>
        {loading && (
          <div style={{ height: '100%', width: '100%', textAlign: 'center', padding: '4rem' }}>
            <Spinner className="page-loader" themeColor="dark-neutral" />
          </div>
        )}

        {!loading && <GenericDetails record={recordDetails} config={RecordDetailsSettings || {}} />}

        <div>
          {!loading && (
            <TabPanel tabId="tab-1" id="panel-1">
              <RecordHistory
                history={{
                  amendments: recordAmendmentHistory,
                  history: recordHistoryList,
                  template: !!RecordDetailsSettings.HistoryConfig
                    ? RecordDetailsSettings.HistoryConfig.filter((t) => t.IsEnabled)
                    : [],
                }}
                requiredVoidRight="CharityVoid"
                module="charity"
              />
            </TabPanel>
          )}
          <TabPanel tabId="tab-2" id="panel-2">
            <NotesPanel
              saveNote={(noteText) => {
                dispatch(saveNewNote(noteText, 1300, recordDetails.CharityRecordId))
              }}
              editNote={(note) => {
                dispatch(editNote(note, 1300, recordDetails.CharityRecordId))
              }}
              deleteNote={(noteId) => {
                dispatch(deleteNote(noteId, 1300, recordDetails.CharityRecordId))
              }}
            />
          </TabPanel>
          {!loading && isUsingBusinessExtension ? (
            <TabPanel tabId="tab-3" id="panel-3">
              <CharityBusinessPanel config={RecordDetailsSettings} record={recordDetails} />
            </TabPanel>
          ) : null}
        </div>
        <RecordDetailPageBackButton />
      </Body>
    </TabProvider>
  )
}

export default CharityRecordDetails
